import { styled } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import Typography from "components/Typography";
import Div from "components/primitives/Div";
import Anchor from "components/Anchor";
import LockIcon from "components/Icons/LockIcon";
import Constants from "config/Constants";
import type { UpsellPageContent } from "../../Upsell.types";
import UpsellTopBanner from "../UpsellTopBanner";
import UpsellFeatureCard from "../UpsellFeatureCard";
import UpsellRequestAccessButton from "../UpsellRequestAccessButton";
import UpsellTrustLogos from "../UpsellTrustLogos";
import useUpsellPageTimeSpent from "../../hooks/useUpsellPageTimeSpent";
import VideoPlayer from "components/VideoPlayer";

const Root = styled(Div)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: "100%",
  backgroundColor: theme.palette.grey[10],
}));

const Content = styled(Div)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(5),
  width: "100%",
  maxWidth: "1040px",
  margin: "0 auto",
  padding: theme.spacing(6, 3, 8),
}));

const Intro = styled(Div)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
}));

/** The grey pill above the heading, naming the feature the coach cannot reach yet. */
const LockedFeaturePill = styled(Div)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 1.75),
  borderRadius: 999,
  backgroundColor: theme.palette.grey[60],
  color: theme.palette.grey[120],

  ".MuiSvgIcon-root": {
    fontSize: "16px",
  },
}));

const LockedFeatureLabel = styled(Typography)({
  fontWeight: 600,
});

const Heading = styled(Typography)({
  textAlign: "center",
  maxWidth: "620px",
});

const Subheading = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[120],
  textAlign: "center",
  maxWidth: "720px",
}));

const Hero = styled(Div)(({ theme }) => ({
  position: "relative",
  width: "100%",
  borderRadius: theme.spacing(2.5),
  overflow: "hidden",
}));

const CardGrid = styled(Div)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: theme.spacing(2),
  width: "100%",
}));

/**
 * `Anchor` hovers to `primary.dark`, which the 2024 theme derives from a black
 * `primary.main` — invisible against this panel. Hover to mint instead.
 */
const CtaEmailLink = styled(Anchor)(({ theme }) => ({
  color: theme.palette.grey[100],

  "&:hover": {
    color: theme.palette.secondary1[60],
  },
}));

interface SupportEmailLinkProps {
  children?: React.ReactNode;
}

/** Supplies the `mailto:` href so the whole sentence can live in one translation key. */
const SupportEmailLink = (props: SupportEmailLinkProps) => (
  <CtaEmailLink href={`mailto:${Constants.emails.support}`}>{props.children}</CtaEmailLink>
);

const CtaPanel = styled(Div)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  /** Drops the CTA below the copy rather than squeezing it on narrow screens. */
  flexWrap: "wrap",
  gap: theme.spacing(4),
  width: "100%",
  padding: theme.spacing(4, 5),
  borderRadius: theme.spacing(2.5),
  backgroundColor: theme.palette.common.black,
}));

const CtaCopy = styled(Div)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

const CtaAction = styled(Div)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const CtaHeading = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
  color: theme.palette.common.white,
}));

/**
 * 15px is not a step on the type scale (`TextS` is 14, `TextM` is 16) — matching
 * the design exactly rather than snapping to a token. Raised with design; if the
 * scale wins, this becomes `TextS` and the override goes away.
 */
const CtaDescription = styled(Typography)(({ theme }) => ({
  fontSize: "15px",
  color: theme.palette.grey[40],
}));

const CtaEmailHint = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[100],
}));

interface UpsellPageLayoutProps {
  content: UpsellPageContent;
}

function UpsellPageLayout(props: UpsellPageLayoutProps) {
  const { content } = props;

  const { t } = useTranslation();

  useUpsellPageTimeSpent(content.feature);

  /**
   * The app root still provides the legacy `muiTheme`. The 2024 theme is what
   * defines the `Text*`/`Heading*` typography variants, the `primary` button
   * variant and the `secondary1`/`grey` palette scales this page is built on,
   * so it has to be scoped here — same as `Sidebar` and the app banners do.
   */
  return (
    <ThemeProvider2024>
      <Root>
        <UpsellTopBanner feature={content.feature} prompt={content.bannerPrompt} />
        <Content>
          <Intro>
            <LockedFeaturePill>
              <LockIcon />
              <LockedFeatureLabel variant="TextS">{t(content.eyebrow)}</LockedFeatureLabel>
            </LockedFeaturePill>
            <Heading variant="HeadingXL">{t(content.heading)}</Heading>
            <Subheading variant="TextL">{t(content.subheading)}</Subheading>
          </Intro>

          <Hero>
            <VideoPlayer
              type={content.video.type}
              videoId={content.video.videoId}
              slotProps={{ root: { sx: { display: "flex", height: "100%" } } }}
              thumbnailTime={content.video.thumbnailTime}
            />
          </Hero>

          <CardGrid>
            {content.cards.map((card) => (
              <UpsellFeatureCard key={card.title} card={card} />
            ))}
          </CardGrid>

          <CtaPanel>
            <CtaCopy>
              {/* `component` set explicitly: HeadingXS maps to h1, and the page title already is one. */}
              <CtaHeading variant="HeadingXS" component="h2">
                {t(content.ctaHeading)}
              </CtaHeading>
              <CtaDescription variant="TextS">{t(content.ctaDescription)}</CtaDescription>
            </CtaCopy>
            <CtaAction>
              <UpsellRequestAccessButton feature={content.feature} tone="mint" origin="ctaPanel" />
              <CtaEmailHint variant="TextXS">
                <Trans i18nKey="upsell.prefer_email_label" components={{ Link: <SupportEmailLink /> }}>
                  Prefer email? <SupportEmailLink>{Constants.emails.support}</SupportEmailLink>
                </Trans>
              </CtaEmailHint>
            </CtaAction>
          </CtaPanel>

          <UpsellTrustLogos />
        </Content>
      </Root>
    </ThemeProvider2024>
  );
}

export default UpsellPageLayout;
